import { create } from 'zustand';
import type { MessageAttachment, ReactionSummary, AcknowledgmentSummary, LinkPreviewData } from '@goodchat/shared/types';

interface MessageAuthor {
  id: string;
  fullName: string;
  displayName: string;
  profilePictureUrl: string | null;
  isExternal?: boolean;
}

interface ReplyTo {
  id: string;
  content: string | null;
  authorDisplayName: string;
  isDeleted: boolean;
}

export interface VoiceMemoData {
  id: string;
  fileUrl: string;
  durationSeconds: number;
  waveformData: number[] | null;
  transcript: string | null;
  expiresAt: string | null;
}

export interface ChatMessage {
  id: string;
  areaId: string;
  threadId: string | null;
  authorId: string;
  content: string | null;
  messageType: string;
  replyToMessageId: string | null;
  linkedThreadId?: string | null;
  sequenceNumber: number;
  isDeleted: boolean;
  editedAt: string | null;
  createdAt: string;
  author: MessageAuthor;
  replyTo: ReplyTo | null;
  requiresAcknowledgment?: boolean;
  attachments?: MessageAttachment[];
  reactions?: ReactionSummary[];
  voiceMemo?: VoiceMemoData | null;
  acknowledgmentSummary?: AcknowledgmentSummary | null;
  linkPreviews?: LinkPreviewData[];
  isPinned?: boolean;
  isBookmarked?: boolean;
  linkPreviewHidden?: boolean;
  readCount?: number;
  poll?: any;
  /** Inline comment threads anchored to this message (design v2). */
  comments?: { threadId: string; isInternal: boolean; count: number; unreadCount: number; lastAt: string }[];
  // Optimistic sending
  tempId?: string;
  isPending?: boolean;
  isFailed?: boolean;
}

interface Typer {
  userId: string;
  displayName: string;
  profilePictureUrl: string | null;
}

interface OfflineQueueItem {
  tempId: string;
  orgId: string;
  areaId: string;
  content: string;
  replyToMessageId?: string;
}

interface ChatState {
  messagesByArea: Record<string, ChatMessage[]>;
  hasMoreByArea: Record<string, boolean>;
  typingByArea: Record<string, Typer[]>;
  isConnected: boolean;
  offlineQueue: OfflineQueueItem[];
  replyingTo: Record<string, ChatMessage | null>;

  setMessages: (areaId: string, messages: ChatMessage[], hasMore: boolean) => void;
  mergeMessages: (areaId: string, messages: ChatMessage[], hasMore: boolean) => void;
  prependMessages: (areaId: string, messages: ChatMessage[], hasMore: boolean) => void;
  addMessage: (areaId: string, message: ChatMessage) => void;
  addPendingMessage: (areaId: string, message: ChatMessage) => void;
  replacePendingMessage: (tempId: string, message: ChatMessage) => void;
  markPendingFailed: (tempId: string) => void;
  removePendingMessage: (tempId: string) => void;
  updateMessageContent: (messageId: string, areaId: string, content: string, editedAt: string) => void;
  markMessageDeleted: (messageId: string, areaId: string) => void;
  setTypers: (areaId: string, typers: Typer[]) => void;
  setConnected: (connected: boolean) => void;
  addToOfflineQueue: (item: OfflineQueueItem) => void;
  clearOfflineQueue: () => OfflineQueueItem[];
  setReplyingTo: (areaId: string, message: ChatMessage | null) => void;
  addReactionToMessage: (messageId: string, areaId: string, emoji: string, user: { userId: string; displayName: string }, currentUserId: string) => void;
  removeReactionFromMessage: (messageId: string, areaId: string, emoji: string, userId: string, currentUserId: string) => void;
  updateAcknowledgment: (messageId: string, areaId: string, total: number, acknowledgedCount: number) => void;
  setLinkPreviews: (messageId: string, areaId: string, previews: LinkPreviewData[]) => void;
  togglePinned: (messageId: string, areaId: string, isPinned: boolean) => void;
  toggleBookmarked: (messageId: string, areaId: string, isBookmarked: boolean) => void;
  updateMessageInArea: (areaId: string, messageId: string, fields: Partial<ChatMessage>) => void;
  /** A comment arrived over the socket: bump the anchor message's summary. */
  applyIncomingComment: (
    areaId: string,
    messageId: string,
    threadId: string,
    isInternal: boolean,
    isOwn: boolean,
  ) => void;
  /** Clear the unread badge on one comment thread. */
  markCommentThreadRead: (areaId: string, messageId: string, threadId: string) => void;
  /** Bumped on pin.added/removed — PinnedMessagesBar refetches when it changes. */
  pinsVersionByArea: Record<string, number>;
  bumpPinsVersion: (areaId: string) => void;
  reset: () => void;
}


const OFFLINE_QUEUE_KEY = 'goodchat_offline_queue';

function persistOfflineQueue(queue: OfflineQueueItem[]): void {
  try {
    if (queue.length === 0) localStorage.removeItem(OFFLINE_QUEUE_KEY);
    else localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue));
  } catch {
    // Storage unavailable (private mode/quota) — queue stays in-memory only
  }
}

function loadOfflineQueue(): OfflineQueueItem[] {
  try {
    const raw = localStorage.getItem(OFFLINE_QUEUE_KEY);
    return raw ? (JSON.parse(raw) as OfflineQueueItem[]) : [];
  } catch {
    return [];
  }
}

export const useChatStore = create<ChatState>((set, get) => ({
  messagesByArea: {},
  hasMoreByArea: {},
  typingByArea: {},
  isConnected: false,
  offlineQueue: loadOfflineQueue(),
  replyingTo: {},
  pinsVersionByArea: {},

  setMessages: (areaId, messages, hasMore) =>
    set((s) => ({
      messagesByArea: { ...s.messagesByArea, [areaId]: messages },
      hasMoreByArea: { ...s.hasMoreByArea, [areaId]: hasMore },
    })),

  // Reconnect refetch: server list wins, but locally-pending/failed optimistic
  // messages not yet on the server are preserved (a plain setMessages would
  // silently discard a message the user is still sending).
  mergeMessages: (areaId, messages, hasMore) =>
    set((s) => {
      const existing = s.messagesByArea[areaId] ?? [];
      const fetchedIds = new Set(messages.map((m) => m.id));
      const pendingLocal = existing.filter(
        (m) => (m.isPending || m.isFailed) && !fetchedIds.has(m.id),
      );
      return {
        messagesByArea: { ...s.messagesByArea, [areaId]: [...messages, ...pendingLocal] },
        hasMoreByArea: { ...s.hasMoreByArea, [areaId]: hasMore },
      };
    }),

  prependMessages: (areaId, messages, hasMore) =>
    set((s) => {
      const existing = s.messagesByArea[areaId] ?? [];
      return {
        messagesByArea: { ...s.messagesByArea, [areaId]: [...messages, ...existing] },
        hasMoreByArea: { ...s.hasMoreByArea, [areaId]: hasMore },
      };
    }),

  addMessage: (areaId, message) =>
    set((s) => {
      const existing = s.messagesByArea[areaId] ?? [];
      // Deduplicate
      if (existing.some((m) => m.id === message.id)) return s;
      return {
        messagesByArea: {
          ...s.messagesByArea,
          [areaId]: [...existing, message],
        },
      };
    }),

  addPendingMessage: (areaId, message) =>
    set((s) => ({
      messagesByArea: {
        ...s.messagesByArea,
        [areaId]: [...(s.messagesByArea[areaId] ?? []), message],
      },
    })),

  replacePendingMessage: (tempId, message) =>
    set((s) => {
      // Only update the area that contains the pending message — avoids cloning all areas
      // and prevents unnecessary re-renders in unrelated area subscribers.
      for (const [areaId, msgs] of Object.entries(s.messagesByArea)) {
        if (msgs.some((m) => m.tempId === tempId || m.id === message.id)) {
          return {
            messagesByArea: {
              ...s.messagesByArea,
              [areaId]: msgs
                .filter((m) => m.tempId !== tempId && m.id !== message.id)
                .concat({ ...message, tempId: undefined, isPending: false, isFailed: false }),
            },
          };
        }
      }
      return s;
    }),

  markPendingFailed: (tempId) =>
    set((s) => {
      for (const [areaId, msgs] of Object.entries(s.messagesByArea)) {
        if (msgs.some((m) => m.tempId === tempId)) {
          return {
            messagesByArea: {
              ...s.messagesByArea,
              [areaId]: msgs.map((m) =>
                m.tempId === tempId ? { ...m, isPending: false, isFailed: true } : m,
              ),
            },
          };
        }
      }
      return s;
    }),

  removePendingMessage: (tempId) =>
    set((s) => {
      for (const [areaId, msgs] of Object.entries(s.messagesByArea)) {
        if (msgs.some((m) => m.tempId === tempId)) {
          return {
            messagesByArea: {
              ...s.messagesByArea,
              [areaId]: msgs.filter((m) => m.tempId !== tempId),
            },
          };
        }
      }
      return s;
    }),

  updateMessageContent: (messageId, areaId, content, editedAt) =>
    set((s) => {
      const msgs = s.messagesByArea[areaId];
      if (!msgs) return s;
      return {
        messagesByArea: {
          ...s.messagesByArea,
          [areaId]: msgs.map((m) =>
            m.id === messageId ? { ...m, content, editedAt } : m,
          ),
        },
      };
    }),

  markMessageDeleted: (messageId, areaId) =>
    set((s) => {
      const msgs = s.messagesByArea[areaId];
      if (!msgs) return s;
      return {
        messagesByArea: {
          ...s.messagesByArea,
          [areaId]: msgs.map((m) =>
            m.id === messageId ? { ...m, isDeleted: true, content: null } : m,
          ),
        },
      };
    }),

  setTypers: (areaId, typers) =>
    set((s) => ({
      typingByArea: { ...s.typingByArea, [areaId]: typers },
    })),

  setConnected: (connected) => set({ isConnected: connected }),

  bumpPinsVersion: (areaId) =>
    set((s) => ({
      pinsVersionByArea: { ...s.pinsVersionByArea, [areaId]: (s.pinsVersionByArea[areaId] ?? 0) + 1 },
    })),

  addToOfflineQueue: (item) =>
    set((s) => {
      const offlineQueue = [...s.offlineQueue, item];
      persistOfflineQueue(offlineQueue);
      return { offlineQueue };
    }),

  clearOfflineQueue: () => {
    const queue = get().offlineQueue;
    set({ offlineQueue: [] });
    persistOfflineQueue([]);
    return queue;
  },

  setReplyingTo: (areaId, message) =>
    set((s) => ({
      replyingTo: { ...s.replyingTo, [areaId]: message },
    })),

  addReactionToMessage: (messageId, areaId, emoji, user, currentUserId) =>
    set((s) => {
      const msgs = s.messagesByArea[areaId];
      if (!msgs) return s;
      return {
        messagesByArea: {
          ...s.messagesByArea,
          [areaId]: msgs.map((m) => {
            if (m.id !== messageId) return m;
            const existing = m.reactions ?? [];
            const idx = existing.findIndex((r) => r.emoji === emoji);
            if (idx >= 0) {
              const r = existing[idx];
              if (r.users.some((u) => u.userId === user.userId)) return m;
              const updated = [...existing];
              updated[idx] = { ...r, count: r.count + 1, users: [...r.users, user], includesMe: r.includesMe || user.userId === currentUserId };
              return { ...m, reactions: updated };
            }
            return { ...m, reactions: [...existing, { emoji, count: 1, users: [user], includesMe: user.userId === currentUserId }] };
          }),
        },
      };
    }),

  removeReactionFromMessage: (messageId, areaId, emoji, userId, currentUserId) =>
    set((s) => {
      const msgs = s.messagesByArea[areaId];
      if (!msgs) return s;
      return {
        messagesByArea: {
          ...s.messagesByArea,
          [areaId]: msgs.map((m) => {
            if (m.id !== messageId) return m;
            const existing = m.reactions ?? [];
            const idx = existing.findIndex((r) => r.emoji === emoji);
            if (idx < 0) return m;
            const r = existing[idx];
            if (r.count <= 1) {
              return { ...m, reactions: existing.filter((_, i) => i !== idx) };
            }
            const updated = [...existing];
            const newUsers = r.users.filter((u) => u.userId !== userId);
            updated[idx] = { ...r, count: r.count - 1, users: newUsers, includesMe: userId === currentUserId ? false : r.includesMe };
            return { ...m, reactions: updated };
          }),
        },
      };
    }),

  updateAcknowledgment: (messageId, areaId, total, acknowledgedCount) =>
    set((s) => {
      const msgs = s.messagesByArea[areaId];
      if (!msgs) return s;
      return {
        messagesByArea: {
          ...s.messagesByArea,
          [areaId]: msgs.map((m) =>
            m.id === messageId && m.acknowledgmentSummary
              ? {
                  ...m,
                  acknowledgmentSummary: {
                    ...m.acknowledgmentSummary,
                    total,
                    acknowledged: acknowledgedCount,
                    pending: Math.max(0, total - acknowledgedCount),
                  },
                }
              : m,
          ),
        },
      };
    }),

  setLinkPreviews: (messageId, areaId, previews) =>
    set((s) => {
      const msgs = s.messagesByArea[areaId];
      if (!msgs) return s;
      return {
        messagesByArea: {
          ...s.messagesByArea,
          [areaId]: msgs.map((m) =>
            m.id === messageId ? { ...m, linkPreviews: previews } : m,
          ),
        },
      };
    }),

  togglePinned: (messageId, areaId, isPinned) =>
    set((s) => {
      const msgs = s.messagesByArea[areaId];
      if (!msgs) return s;
      return {
        messagesByArea: {
          ...s.messagesByArea,
          [areaId]: msgs.map((m) =>
            m.id === messageId ? { ...m, isPinned } : m,
          ),
        },
      };
    }),

  toggleBookmarked: (messageId, areaId, isBookmarked) =>
    set((s) => {
      const msgs = s.messagesByArea[areaId];
      if (!msgs) return s;
      return {
        messagesByArea: {
          ...s.messagesByArea,
          [areaId]: msgs.map((m) =>
            m.id === messageId ? { ...m, isBookmarked } : m,
          ),
        },
      };
    }),

  applyIncomingComment: (areaId, messageId, threadId, isInternal, isOwn) =>
    set((s) => {
      const list = s.messagesByArea[areaId];
      if (!list) return s;
      return {
        messagesByArea: {
          ...s.messagesByArea,
          [areaId]: list.map((m) => {
            if (m.id !== messageId) return m;
            const existing = m.comments ?? [];
            const found = existing.find((c) => c.threadId === threadId);
            // Your own comment is already in the card's local list, so it must
            // not also arrive as unread — otherwise replying to yourself lights
            // up a badge.
            const unreadDelta = isOwn ? 0 : 1;
            const comments = found
              ? existing.map((c) =>
                  c.threadId === threadId
                    ? { ...c, count: c.count + 1, unreadCount: c.unreadCount + unreadDelta, lastAt: new Date().toISOString() }
                    : c,
                )
              : [
                  ...existing,
                  {
                    threadId,
                    isInternal,
                    count: 1,
                    unreadCount: unreadDelta,
                    lastAt: new Date().toISOString(),
                  },
                ];
            return { ...m, comments };
          }),
        },
      };
    }),

  markCommentThreadRead: (areaId, messageId, threadId) =>
    set((s) => {
      const list = s.messagesByArea[areaId];
      if (!list) return s;
      return {
        messagesByArea: {
          ...s.messagesByArea,
          [areaId]: list.map((m) =>
            m.id === messageId
              ? {
                  ...m,
                  comments: (m.comments ?? []).map((c) =>
                    c.threadId === threadId ? { ...c, unreadCount: 0 } : c,
                  ),
                }
              : m,
          ),
        },
      };
    }),

  updateMessageInArea: (areaId, messageId, fields) =>
    set((s) => {
      const msgs = s.messagesByArea[areaId];
      if (!msgs) return s;
      return {
        messagesByArea: {
          ...s.messagesByArea,
          [areaId]: msgs.map((m) =>
            m.id === messageId ? { ...m, ...fields } : m,
          ),
        },
      };
    }),

  reset: () =>
    set({
      messagesByArea: {},
      hasMoreByArea: {},
      typingByArea: {},
      isConnected: false,
      offlineQueue: [],
      replyingTo: {},
    }),
}));
